import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

export interface InlineSuggestionItem {
	id: string;
	label: string;
	hintPrefix?: string;
}

export interface InlineSuggestionOptions {
	onAccept?: (item: InlineSuggestionItem) => void;
}

export interface InlineSuggestionStorage {
	selectedItem: InlineSuggestionItem | null;
}

export const InlineSuggestionPluginKey = new PluginKey('inlineSuggestion');

export const InlineSuggestion = Extension.create<InlineSuggestionOptions, InlineSuggestionStorage>({
	name: 'inlineSuggestion',

	addOptions() {
		return {
			onAccept: undefined,
		};
	},

	addStorage() {
		return {
			selectedItem: null,
		};
	},

	addCommands() {
		return {
			setSelectedSuggestion: (item: InlineSuggestionItem | null) => ({ editor }) => {
				this.storage.selectedItem = item;
				editor.view.dispatch(editor.state.tr);
				return true;
			},

			acceptSuggestion: () => () => {
				const item = this.storage.selectedItem;

				if (item) {
					this.options.onAccept?.(item);
					this.storage.selectedItem = null;
					return true;
				}

				return false;
			},
		};
	},

	addProseMirrorPlugins() {
		const storage = this.storage;
		const options = this.options;

		return [
			new Plugin({
				key: InlineSuggestionPluginKey,
				props: {
					handleKeyDown: (_view, event) => {
						if (event.key === 'Tab' && storage.selectedItem) {
							event.preventDefault();
							options.onAccept?.(storage.selectedItem);
							storage.selectedItem = null;
							return true;
						}

						return false;
					},
					decorations(state) {
						const item = storage.selectedItem;
						if (!item?.hintPrefix) return DecorationSet.empty;

						const { selection, doc } = state;
						if (!selection.empty) return DecorationSet.empty;

						let textBefore = '';

						doc.nodesBetween(0, selection.head, (node) => {
							if (node.type.name === 'text') {
								textBefore += node.text;
							}
						});

						textBefore = textBefore.trim().toLowerCase();

						const hintLower = item.hintPrefix.toLowerCase();

						if (!hintLower.startsWith(textBefore) || textBefore.length === 0) {
							return DecorationSet.empty;
						}

						if (textBefore === hintLower) {
							return DecorationSet.empty;
						}

						const remainingHint = item.hintPrefix.slice(textBefore.length);

						const widget = Decoration.widget(selection.head, () => {
							const span = document.createElement('span');
							span.textContent = remainingHint;
							span.className = 'text-dimmed pointer-events-none select-none';
							span.style.opacity = '0.5';
							return span;
						}, { side: 1 });

						return DecorationSet.create(doc, [widget]);
					},
				},
			}),
		];
	},
});

declare module '@tiptap/core' {
	interface Commands<ReturnType> {
		inlineSuggestion: {
			setSelectedSuggestion: (item: InlineSuggestionItem | null) => ReturnType;
			acceptSuggestion: () => ReturnType;
		};
	}
}
